import moment from 'moment-timezone';
import { isNotNullOrUndefined, isStringNotEmpty } from './objectUtils';

export const SLA_METRIC_VERSION = 2;

export const DEFAULT_RESPONSE_SLA_MINUTES = {
    email: 24 * 60,
    sms: 4 * 60,
    missedCall: 2 * 60,
    voicemail: 4 * 60
};

export const DEFAULT_RESOLUTION_SLA_MINUTES = {
    email: 48 * 60,
    sms: 24 * 60,
    missedCall: 24 * 60,
    voicemail: 24 * 60
};

export const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value?.toDate === 'function') return value.toDate();
    if (value?.seconds !== undefined) {
        return new Date((value.seconds * 1000) + Math.floor((value.nanoseconds || 0) / 1000000));
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const roundTo = (value, precision = 1) => {
    if (value === null || value === undefined || Number.isNaN(value)) return null;
    const multiplier = Math.pow(10, precision);
    return Math.round(value * multiplier) / multiplier;
};

export const getDurationMinutes = (start, end) => {
    const startDate = toDate(start);
    const endDate = toDate(end);
    if (!startDate || !endDate || endDate < startDate) return null;
    return roundTo((endDate.getTime() - startDate.getTime()) / 60000);
};

export const getSlaOwnerId = (record = {}, repliedBy = null) => {
    if (isStringNotEmpty(repliedBy) && repliedBy !== 'SYSTEM') return repliedBy;
    if (isStringNotEmpty(record.repliedBy) && record.repliedBy !== 'SYSTEM') return record.repliedBy;
    if (isStringNotEmpty(record.repId) && record.repId !== 'SYSTEM') return record.repId;
    if (isStringNotEmpty(record.assignedTo) && record.assignedTo !== 'SYSTEM') return record.assignedTo;
    return 'NONE';
};

const getSortedStatusHistory = (task = {}) => {
    return Array.isArray(task.statusHistory)
        ? task.statusHistory
            .map((entry) => ({ ...entry, atDate: toDate(entry?.at) }))
            .filter((entry) => entry.atDate)
            .sort((left, right) => left.atDate - right.atDate)
        : [];
};

const getFirstTouchAt = (task = {}) => {
    return getSortedStatusHistory(task).find((entry) => ['IN_PROGRESS', 'COMPLETED'].includes(entry.status))?.atDate || null;
};

const getTaskSlaChannel = (task = {}) => {
    if (task.category === 'MISSED_CALL') return 'missedCall';
    if (task.category === 'VOICEMAIL') return 'voicemail';
    return null;
};

export const buildTaskSlaMetricFields = ({ task = {}, firstTouchAt = null, completedAt = null, targetMinutes = null } = {}) => {
    const channel = getTaskSlaChannel(task);
    if (!channel) return {};

    const responseTargetMinutes = isNotNullOrUndefined(targetMinutes) ? targetMinutes : DEFAULT_RESPONSE_SLA_MINUTES[channel];
    const resolvedFirstTouchAt = toDate(firstTouchAt) || getFirstTouchAt(task);
    const resolvedCompletedAt = toDate(completedAt) || toDate(task.completedAt);
    const responseMinutes = getDurationMinutes(task.createdAt, resolvedFirstTouchAt);
    const resolutionMinutes = getDurationMinutes(task.createdAt, resolvedCompletedAt);
    const hasResponse = responseMinutes !== null;
    const hasResolution = resolutionMinutes !== null;
    const resolutionTargetMinutes = DEFAULT_RESOLUTION_SLA_MINUTES[channel];

    return {
        slaOwnerId: getSlaOwnerId(task),
        slaChannel: channel,
        slaResponded: hasResponse,
        slaResponseAt: hasResponse ? resolvedFirstTouchAt : null,
        slaResponseMinutes: hasResponse ? responseMinutes : null,
        slaResponseTargetMinutes: responseTargetMinutes,
        slaResponseBreached: hasResponse ? responseMinutes > responseTargetMinutes : null,
        slaResponseWithinTarget: hasResponse ? responseMinutes <= responseTargetMinutes : null,
        slaResolved: hasResolution,
        slaResolutionAt: hasResolution ? resolvedCompletedAt : null,
        slaResolutionMinutes: hasResolution ? resolutionMinutes : null,
        slaResolutionTargetMinutes: resolutionTargetMinutes,
        slaResolutionBreached: hasResolution ? resolutionMinutes > resolutionTargetMinutes : null,
        slaResolutionWithinTarget: hasResolution ? resolutionMinutes <= resolutionTargetMinutes : null,
        slaMetricVersion: SLA_METRIC_VERSION
    };
};

export const buildCommunicationSlaMetricFields = ({
    record = {},
    channel,
    repliedAt = null,
    repliedBy = null,
    resolvedAt = null,
    targetMinutes = DEFAULT_RESPONSE_SLA_MINUTES[channel]
} = {}) => {
    const resolvedRepliedAt = toDate(repliedAt || record.repliedAt);
    const resolvedResolvedAt = toDate(resolvedAt || record.resolvedAt);
    const responseMinutes = getDurationMinutes(record.createdAt, resolvedRepliedAt);
    const resolutionMinutes = getDurationMinutes(record.createdAt, resolvedResolvedAt);
    const hasResponse = responseMinutes !== null;
    const hasResolution = resolutionMinutes !== null;
    const resolutionTargetMinutes = DEFAULT_RESOLUTION_SLA_MINUTES[channel];

    return {
        slaOwnerId: getSlaOwnerId(record, repliedBy),
        slaChannel: channel,
        slaResponded: hasResponse,
        slaResponseAt: hasResponse ? resolvedRepliedAt : null,
        slaResponseMinutes: hasResponse ? responseMinutes : null,
        slaResponseTargetMinutes: targetMinutes,
        slaResponseBreached: hasResponse ? responseMinutes > targetMinutes : null,
        slaResponseWithinTarget: hasResponse ? responseMinutes <= targetMinutes : null,
        slaResolved: hasResolution,
        slaResolutionAt: hasResolution ? resolvedResolvedAt : null,
        slaResolutionMinutes: hasResolution ? resolutionMinutes : null,
        slaResolutionTargetMinutes: resolutionTargetMinutes,
        slaResolutionBreached: hasResolution ? resolutionMinutes > resolutionTargetMinutes : null,
        slaResolutionWithinTarget: hasResolution ? resolutionMinutes <= resolutionTargetMinutes : null,
        slaMetricVersion: SLA_METRIC_VERSION
    };
};

export const getOpenSlaBreachedCutoff = (targetMinutes, now = new Date()) => {
    return moment(toDate(now) || new Date()).subtract(targetMinutes, 'minutes').toDate();
};
